// Column layout for the stock matrix. A channel with two or more warehouses
// starts folded into one combined column; opening it shows one column per
// warehouse again.

export interface MatrixWarehouse {
  external_id: string;
  channel_id: string | null;
  channel_name: string | null;
}

export type MatrixColumn<W extends MatrixWarehouse> =
  | { kind: "warehouse"; key: string; warehouse: W }
  | { kind: "channel"; key: string; channelName: string | null; warehouses: W[] };

// The header band over one channel's columns.
export interface ChannelBand {
  channelId: string | null;
  name: string | null;
  // Columns drawn under the band: 1 while folded.
  columnCount: number;
  // Two or more warehouses in a known channel, so the band can open and close.
  foldable: boolean;
  open: boolean;
}

export interface MatrixLayout<W extends MatrixWarehouse> {
  columns: MatrixColumn<W>[];
  bands: ChannelBand[];
  // Key of the last column under each band, which gets a heavier right border.
  bandEndKeys: Set<string>;
}

// `warehouses` arrive ordered by org-node tree position, so one channel is one
// consecutive run.
export function buildMatrixLayout<W extends MatrixWarehouse>(
  warehouses: W[],
  openChannelIds: Set<string>,
): MatrixLayout<W> {
  const runs: W[][] = [];
  for (const w of warehouses) {
    const last = runs[runs.length - 1];
    if (last && last[0].channel_id === w.channel_id) last.push(w);
    else runs.push([w]);
  }

  const columns: MatrixColumn<W>[] = [];
  const bands: ChannelBand[] = [];
  const bandEndKeys = new Set<string>();
  for (const run of runs) {
    const { channel_id: channelId, channel_name: name } = run[0];
    const foldable = run.length > 1 && channelId !== null;
    const open = channelId === null || run.length === 1 || openChannelIds.has(channelId);
    if (open) {
      for (const w of run) columns.push({ kind: "warehouse", key: w.external_id, warehouse: w });
    } else {
      columns.push({ kind: "channel", key: `channel:${channelId}`, channelName: name, warehouses: run });
    }
    bandEndKeys.add(columns[columns.length - 1].key);
    bands.push({ channelId, name, columnCount: open ? run.length : 1, foldable, open });
  }
  return { columns, bands, bandEndKeys };
}

// Stock shown in one column: the warehouse's own stock, or the folded
// channel's warehouses added together. Undefined when there is none.
export function columnStock<W extends MatrixWarehouse>(
  stocks: Record<string, number>,
  column: MatrixColumn<W>,
): number | undefined {
  if (column.kind === "warehouse") return stocks[column.warehouse.external_id];
  let sum = 0;
  for (const w of column.warehouses) sum += stocks[w.external_id] ?? 0;
  return sum === 0 ? undefined : sum;
}
